// Builds the compare-to-canonical (YouGov BrandIndex) layer folded into Dependent
// Variable, Brand Equity's YouGov stage only -- same shape as gqv-ground-truth-sql.ts's
// single-metric pattern (always-visible aggregate deviation + a copy-pasteable SQL
// block), extended to 4 metrics (awareness/consideration/buzz/purchase intent) sharing
// one chart instead of one metric alone.

export interface BrandIndexMetric {
  key: string;
  label: string;
}

export interface BrandIndexGroundTruthWeek {
  weekIso: string;
  // This week's MMM value per metric key (WeekRow.perBrandHealthVar).
  mmmByMetric: Record<string, number>;
  // Already-loaded canonical value per metric key for the same week
  // (WeekRow.perBrandHealthVarCanonical) -- only used for the aggregate deviation
  // readout below, not the SQL itself (that re-derives the canonical value live from
  // Redshift).
  canonicalByMetric?: Record<string, number>;
}

// Training-window-wide deviation per metric: sum every week's MMM and canonical values
// first, then compare the totals (MMM / canonical - 1) -- same rationale as
// computeGqvAggregateDeviation/computeAggregateDeviation (not an average of per-week
// ratios, which would let a handful of noisy weeks dominate). Returns undefined when NO
// metric has a canonical total to divide by (no YouGov ground truth data at all);
// otherwise returns one entry per metric, individually 0 if that specific metric's own
// canonical total happens to be 0.
//
// A week's canonicalByMetric is either fully present or entirely undefined (YouGov
// BrandIndex ground truth commonly lags the model's own recent weeks -- see this
// interface's own doc comment), so mmm is only summed for weeks where canonicalByMetric
// is defined too, rather than summed unconditionally. Otherwise the numerator covers
// more weeks than the denominator whenever there's a coverage gap, skewing the ratio
// purely from the gap, not a real mismatch.
export const computeBrandIndexAggregateDeviation = (
  weeks: BrandIndexGroundTruthWeek[],
  metrics: BrandIndexMetric[]
): Record<string, number> | undefined => {
  const totals = new Map(metrics.map(m => [m.key, { mmm: 0, canonical: 0 }]));
  for (const week of weeks) {
    if (!week.canonicalByMetric) continue;
    for (const metric of metrics) {
      const t = totals.get(metric.key)!;
      t.mmm += week.mmmByMetric[metric.key] ?? 0;
      t.canonical += week.canonicalByMetric[metric.key] ?? 0;
    }
  }
  const hasAnyCanonical = Array.from(totals.values()).some(t => t.canonical > 0);
  if (!hasAnyCanonical) {
    return undefined;
  }
  const result: Record<string, number> = {};
  for (const metric of metrics) {
    const t = totals.get(metric.key)!;
    result[metric.key] = t.canonical > 0 ? t.mmm / t.canonical - 1 : 0;
  }
  return result;
};

// Same convention as data-qa-utils.ts's bucketEndDate / ground-truth-sql.ts's
// dateTruncUnitFor (duplicated, not imported -- this module is deliberately
// standalone). Must match weeks[].weekIso's own bucket grain (bucketWeeksByGranularity,
// backend), or the join below silently compares mismatched grains.
const dateTruncUnitFor = (reviewGranularity: string | undefined): 'week' | 'month' | 'day' => {
  const granularity = (reviewGranularity ?? '').trim().toLowerCase();
  if (granularity === 'weekly') return 'week';
  if (granularity === 'monthly') return 'month';
  return 'day';
};

interface BuildBrandIndexGroundTruthSqlArgs {
  company: string;
  metrics: BrandIndexMetric[];
  // Should already be scoped to the model's training window (fencepost) -- weeks
  // outside it aren't part of what the model trains on.
  weeks: BrandIndexGroundTruthWeek[];
  // dataset.reviewGranularity -- see dateTruncUnitFor above. Defaults to weekly (the
  // pre-fix bucketing) when omitted, rather than silently degrading to daily for an
  // existing caller that hasn't been updated yet.
  reviewGranularity?: string;
}

// Uses the fully-qualified `main_db.public.yougov_by_age_gender_dma` (unlike the
// backend's own bare, unqualified table name in mmmDataQA.ts's fetchYouGovGroundTruth):
// this SQL runs from an external DataGrip session against a different cluster
// ("mobius"), which needs the main_db.public. qualifier to reach the shared table --
// same internal-vs-external-qualifier situation as gqv_v2's `streaming_gqv.` schema, per
// Sean's own original SQL pattern (2026-08-29). `metric.label` doubles as the exact
// Redshift metric string (both sides use the same Title Case values, e.g. "Awareness"),
// so no separate mapping is needed here the way YOUGOV_BRAND_INDEX_METRICS' own
// redshiftMetric field is on the backend.
export const buildBrandIndexGroundTruthSql = ({
  company,
  metrics,
  weeks,
  reviewGranularity = 'weekly'
}: BuildBrandIndexGroundTruthSqlArgs): string => {
  if (weeks.length === 0) {
    return "-- No weeks in the model's training window yet.";
  }

  const truncUnit = dateTruncUnitFor(reviewGranularity);
  const escapedCompany = company.replace(/'/g, "''");
  const metricsList = metrics.map(m => `'${m.label.replace(/'/g, "''")}'`).join(', ');
  const mmmRows = weeks
    .flatMap(w =>
      metrics.map(
        m =>
          `  select '${w.weekIso}' as week, '${m.label.replace(/'/g, "''")}' as metric, ${(w.mmmByMetric[m.key] ?? 0).toFixed(4)} as mmm_value`
      )
    )
    .join('\n  union all\n');

  return [
    'with daily as (',
    '  select',
    '    date::date as date,',
    '    metric,',
    '    sum(positives * volume) / nullif(sum(volume), 0) as percent_positive',
    '  from main_db.public.yougov_by_age_gender_dma',
    `  where lower(company) = '${escapedCompany}'`,
    `    and metric in (${metricsList})`,
    '  group by date, metric',
    '),',
    'moving_average as (',
    '  select',
    '    date,',
    '    metric,',
    '    avg(percent_positive) over (',
    '      partition by metric order by date rows between 13 preceding and current row',
    '    ) as moving_average_14day',
    '  from daily',
    '),',
    'canonical as (',
    '  select',
    `    to_char(date_trunc('${truncUnit}', date), 'YYYY-MM-DD') as week,`,
    '    metric,',
    '    sum(moving_average_14day) as canonical_value',
    '  from moving_average',
    '  group by 1, 2',
    '),',
    'mmm as (',
    mmmRows,
    ')',
    'select',
    '  m.week,',
    '  m.metric,',
    '  m.mmm_value, c.canonical_value, round(m.mmm_value / nullif(c.canonical_value, 0) - 1, 4) as deviation',
    'from mmm m',
    'left join canonical c on c.week = m.week and c.metric = m.metric',
    'order by m.week, m.metric;'
  ].join('\n');
};
